import type {CSSProperties} from 'react';
import {TOUCH_MIN} from '../scale.js';
import {WEDGE_LOOK_FAMILY_CSS} from './wedgeLookRegistry.js';

export {FOLLOW_UP_TOGGLE_HIT_WIDTH, TERRITORY_REMOVE_HIT_HEIGHT} from './wedgeHitSizes.js';
export {wedgeSettingsDrawsHit, wedgeSettingsHitAttr} from './wedgeLookRegistry.js';

/* Keep the transparent native target below direct content in its own stacking context. */
export const wedgeSettingsHitStyle: CSSProperties = {position:'relative',isolation:'isolate'};

/* The hit sheet. One base rule draws the 44px target behind every look. The Button renders `data-rv-hit` from the registry
   (`wedgeSettingsHitAttr`), so the opt-outs are attribute rules and no selector list grows with a family:
   - `own`: the look owns its `::before` (a ring, a badge), so the base rule skips it. The `:where` keeps the base rule at the
     specificity it always had, so the rule of the owning sheet is still the only one on the pseudo element.
   - `none`: the look draws no transparent target; its content is cleared.
   Size exceptions (Territory remove, the follow-up toggle) come from the family records, in registry order. The reasons for each
   opt-out are in the comment above `wedgeSettingsDrawsHit` in wedgeLookRegistry.ts and in the family files. */
/* The Deal builder result actions and saved rows keep the region. It is as wide as the button, so it grows above and below only. Copy
   pitch has 12px of gap above it and 12px of padding below. A saved row sits in a grid 8px apart. The three one-pager actions wrap in a
   flex row 8px apart, so two wrapped rows stay clear while each box is 36px or taller: each region then grows 4px or less into the gap.
   A browser check from 320px to 1440px found no overlap between any two regions on the screen; there the one-pager rows hold a 36px box,
   so the regions of two wrapped rows meet at the middle of the gap. Copy one-pager is 34px with no border: below about 311px wide (319px
   with the native label) it sits alone on its line and its region reaches 1px into the next row. */

/* The route stop row packs its Navigate, Call and Move boxes 40px wide and 4px apart, so their 44px regions meet at the middle of each gap and
   never overlap; vertically each stays inside the 10px of row padding. They keep the region. The stop name is bare text inside the slider
   that carries the swipe, which clips at its edge and sits above the subject line, so a 44px region there would be cut and would take taps
   from the line below. `route-stop-open` draws no transparent target, no anchor and no stacking context: its text box is its target, as legacy.

   The Company truth chips wrap 7px apart, each a pill with 6px of padding round an 18px remove mark. A 44px region reaches 13px past the
   mark, and the mark of a chip in the next row is 19px away, so two regions would overlap by 7px and a tap near an edge could remove the
   wrong chip. The remove look draws no transparent target and no stacking context: the tap target is the round visible box, as legacy.

   The Industry settings looks stay in dense wrapping stacks: sub-category chips and brand chips sit 6px apart, and Clear list sits under
   the gap rows. A 44px region would overlap a neighbor, and a tap near an edge could toggle or remove the wrong item, or clear the list.
   They draw no transparent target and no stacking context: the tap target is the rounded visible box, and the text renders as
   shipped. A target clipped to the box still squared the rounded corners, and the stacking context changed the text anti-aliasing.

   The journal opt-out skips `journal-back` and `journal-organize`: their regions clear every neighbor, so they keep the base rule above. */
export const WEDGE_SETTINGS_ACTION_HIT_CSS = `
[data-rv-button][data-rv-appearance="wedge-settings-action"][data-rv-settings-look]:not(:where([data-rv-hit~="own"]))::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 100%;
  height: 100%;
  min-width: ${TOUCH_MIN}px;
  min-height: ${TOUCH_MIN}px;
  transform: translate(-50%, -50%);
  z-index: -1;
  background: transparent;
  border: 0;
  padding: 0;
  pointer-events: auto;
}${WEDGE_LOOK_FAMILY_CSS}
[data-rv-button][data-rv-appearance="wedge-settings-action"][data-rv-hit~="none"]::before {
  content: none;
}`;
